// ══════════════════════════════════════════════════════════════════════════════════════════════
//  EL AVISO DEL FINAL DE LA CUENTA ATRÁS · SONIDO, TÍTULO DE LA PESTAÑA, NOTIFICACIÓN Y PARPADEO  (t271, 16-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  Lo que pasa fuera de la pantalla cuando la cuenta atrás llega a cero (lo lanza context/RelojContext.jsx, una vez por
//  aviso). Todo con la ventana del navegador que se le da, para poder medirlo sin navegador
//  (backend/routes/reloj-cuenta-atras.test.js), y todo con try/catch: si algo de esto no existe (un navegador viejo, la
//  app del móvil, un permiso que falta), el aviso de la pantalla sale igual.
//
//   · EL SONIDO: tres pitidos cortos, cuatro veces (unos seis segundos), con Web Audio, sin ficheros. Los navegadores no
//     dejan sonar nada que no haya empezado con un toque de la persona, así que el «altavoz» se prepara al pulsar
//     «Empezar», «Seguir», «+1 min» o un tiempo rápido (`prepararSonido`) y al final ya puede sonar aunque la pestaña
//     esté escondida. Se apaga con «Parar» o con la casilla «Sonido al terminar».
//   · EL TÍTULO DE LA PESTAÑA: si la pestaña está escondida, parpadea «Tiempo cumplido» hasta que se vuelve a ella. Si
//     mientras tanto la app cambia el título (el número de mensajes sin leer, components/Layout.jsx), se respeta: se
//     parpadea con el nuevo y al terminar se deja el que haya.
//   · LA NOTIFICACIÓN DEL NAVEGADOR: solo si ya hay permiso (aquí no se pide), y solo con la pestaña escondida.
//   · EL PARPADEO DE LA PANTALLA: `modoDestello` dice si parpadea o, con «reducir movimiento» en el sistema
//     (prefers-reduced-motion), se queda un fondo de color fijo.

export const TXT_TIEMPO_CUMPLIDO = 'Tiempo cumplido'

// ── ¿PARPADEO O COLOR FIJO? ─────────────────────────────────────────────────────────────────────
export function prefiereSinMovimiento(win) {
  try { return !!(win && typeof win.matchMedia === 'function' && win.matchMedia('(prefers-reduced-motion: reduce)').matches) } catch (_) { return false }
}
export function modoDestello(win) { return prefiereSinMovimiento(win) ? 'fijo' : 'parpadeo' }

// ── EL SONIDO ───────────────────────────────────────────────────────────────────────────────────
let _ctx = null
// Crea (o despierta) el altavoz. Llamarlo DESDE un toque de la persona: es lo que lo deja sonar después.
export function prepararSonido(win) {
  try {
    const w = win || (typeof window !== 'undefined' ? window : null)
    if (!w) return null
    const AC = w.AudioContext || w.webkitAudioContext
    if (!AC) return null
    if (!_ctx) _ctx = new AC()
    if (_ctx.state === 'suspended' && typeof _ctx.resume === 'function') { const p = _ctx.resume(); if (p && p.catch) p.catch(() => {}) }
    return _ctx
  } catch (_) { return null }
}

// Programa los pitidos de una vez con el reloj del propio audio (no lo frena el navegador en una pestaña escondida).
// Devuelve cómo callarlos.
export function sonarAviso(win, { veces = 4, cada = 1.5, frecuencia = 880 } = {}) {
  const c = prepararSonido(win)
  if (!c) return () => {}
  const sonando = []
  try {
    const t0 = c.currentTime + 0.05
    for (let r = 0; r < veces; r++) {
      for (let i = 0; i < 3; i++) {
        const t = t0 + r * cada + i * 0.22
        const o = c.createOscillator()
        const g = c.createGain()
        o.type = 'sine'
        o.frequency.value = frecuencia
        g.gain.setValueAtTime(0.0001, t)
        g.gain.exponentialRampToValueAtTime(0.25, t + 0.02)
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16)
        o.connect(g)
        g.connect(c.destination)
        o.start(t)
        o.stop(t + 0.18)
        sonando.push(o)
      }
    }
  } catch (_) { /* sin sonido: el aviso de la pantalla sale igual */ }
  return () => { for (const o of sonando) { try { o.stop() } catch (_) { /* ya había parado */ } } }
}

// ── EL TÍTULO DE LA PESTAÑA ─────────────────────────────────────────────────────────────────────
// Alterna «Tiempo cumplido» con el título de la app cada `cada` ms. Devuelve cómo pararlo (deja el título de la app).
export function parpadearTitulo(doc, { texto = TXT_TIEMPO_CUMPLIDO, cada = 1000, temporizador } = {}) {
  if (!doc) return () => {}
  const tmp = temporizador || { cada: (f, ms) => setInterval(f, ms), parar: (id) => clearInterval(id) }
  let base = ''
  let puesto = null
  let encendido = false
  try { base = doc.title } catch (_) { base = '' }
  const paso = () => {
    try {
      // Alguien (la app) lo ha cambiado desde la última vez: ese es el título bueno.
      if (puesto !== null && doc.title !== puesto) base = doc.title
      encendido = !encendido
      puesto = encendido ? texto : base
      doc.title = puesto
    } catch (_) { /* sin título */ }
  }
  paso()
  const id = tmp.cada(paso, cada)
  return () => {
    try { tmp.parar(id) } catch (_) { /* ya no estaba */ }
    try { if (doc.title === puesto) doc.title = base } catch (_) { /* sin título */ }
  }
}

// ── LA NOTIFICACIÓN DEL NAVEGADOR ───────────────────────────────────────────────────────────────
// Solo con el permiso YA dado. Devuelve true si se ha mandado.
export function notificar(win, { titulo = TXT_TIEMPO_CUMPLIDO, cuerpo = '' } = {}) {
  try {
    const N = win && win.Notification
    if (!N || N.permission !== 'granted') return false
    const op = { body: cuerpo, tag: 'biango-reloj', renotify: true }
    try {
      const n = new N(titulo, op)
      n.onclick = () => { try { win.focus(); n.close() } catch (_) { /* nada */ } }
      return true
    } catch (_) {
      // Chrome en Android no deja crearla directamente: va por el service worker de la app.
      const sw = win.navigator && win.navigator.serviceWorker
      if (sw && typeof sw.getRegistration === 'function') {
        sw.getRegistration().then((r) => { if (r && r.showNotification) return r.showNotification(titulo, op) }).catch(() => {})
        return true
      }
      return false
    }
  } catch (_) { return false }
}
